import {
  GENERATION_SCREENING_EXTRACTOR_VERSION,
  type GenerationImpactScenarioInput,
  type GenerationScreeningDocumentFact,
} from '../../types/generationImpact';
import type { SiteDocument } from '../../types/siteRag';

export type ProjectDutyEnvelopeKey =
  | 'generationBlockEnvelope'
  | 'coolingEnvelope'
  | 'switchyardEnvelope'
  | 'ccsCaptureEnvelope'
  | 'ccsCompressionEnvelope'
  | 'naturalGasRouteEnvelope'
  | 'co2ExportRouteEnvelope'
  | 'constructionLaydownEnvelope';

export type ProjectDutyPrefillField =
  | 'referenceIsoGrossMWPerBlock'
  | 'ambientCurve'
  | 'auxiliaryLoadMWPerBlock'
  | 'heatRateBtuPerKWhHhv'
  | 'waterMode'
  | 'waterWithdrawal'
  | 'waterConsumption'
  | 'acousticMode'
  | 'acousticLevelDbA'
  | 'acousticReferenceDistanceM'
  | `envelopes.${ProjectDutyEnvelopeKey}.lengthM`
  | `envelopes.${ProjectDutyEnvelopeKey}.widthM`;

export interface ProjectDutyDocumentPrefillSource {
  id: string;
  documentId: string;
  fileName: string;
  sha256: string;
  pages: number[];
  excerpts: string[];
}

export interface ProjectDutyDocumentPrefill {
  values: {
    referenceIsoGrossMWPerBlock?: number;
    ambientCurve?: Array<{ dryBulbC: number; grossOutputFactor: number }>;
    auxiliaryLoadMWPerBlock?: number;
    heatRateBtuPerKWhHhv?: number;
    water?: {
      withdrawalGalPerMWhGross: number;
      consumptionGalPerMWhGross: number;
    };
    envelopes?: Partial<Record<ProjectDutyEnvelopeKey, { lengthM: number; widthM: number }>>;
    acoustic?: {
      mode: 'sound-power' | 'reference-spl';
      levelDbA: number;
      referenceDistanceM?: number;
    };
  };
  fieldSourceIds: Partial<Record<ProjectDutyPrefillField, string[]>>;
  sources: ProjectDutyDocumentPrefillSource[];
  reviewOnlyCcsFacts: Array<{
    field: string;
    value: number;
    unit: string;
    sourceId: string;
    documentId: string;
    fileName: string;
    page?: number;
    excerpt: string;
  }>;
  conflicts: string[];
}

interface BoundFact {
  document: SiteDocument;
  fact: GenerationScreeningDocumentFact;
  value: number;
  sourceId: string;
}

interface ResolvedValue {
  value: number;
  facts: BoundFact[];
}

interface ResolveProjectDutyDocumentPrefillOptions {
  input: GenerationImpactScenarioInput;
  documents: readonly SiteDocument[];
  evidenceMode?: 'ai-only' | 'docs-included';
}

const SHA256_PATTERN = /^[a-f\d]{64}$/i;

const ENVELOPE_GROUPS: Record<string, ProjectDutyEnvelopeKey> = {
  generationblock: 'generationBlockEnvelope',
  generationblockenvelope: 'generationBlockEnvelope',
  installedgenerationblock: 'generationBlockEnvelope',
  installedgenerationblockenvelope: 'generationBlockEnvelope',
  coolingenvelope: 'coolingEnvelope',
  coolingsystem: 'coolingEnvelope',
  switchyard: 'switchyardEnvelope',
  switchyardenvelope: 'switchyardEnvelope',
  ccscapture: 'ccsCaptureEnvelope',
  ccscaptureenvelope: 'ccsCaptureEnvelope',
  ccscompression: 'ccsCompressionEnvelope',
  ccscompressionenvelope: 'ccsCompressionEnvelope',
  naturalgasroute: 'naturalGasRouteEnvelope',
  naturalgasrouteenvelope: 'naturalGasRouteEnvelope',
  co2exportroute: 'co2ExportRouteEnvelope',
  co2exportrouteenvelope: 'co2ExportRouteEnvelope',
  constructionlaydown: 'constructionLaydownEnvelope',
  constructionlaydownenvelope: 'constructionLaydownEnvelope',
};

function normalizedToken(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function sourceId(sha256: string): string {
  return `document-${sha256.toLowerCase()}`;
}

function documentIsEligible(document: SiteDocument): boolean {
  const sha256 = document.contentHash?.trim().toLowerCase();
  const extraction = document.generationScreeningExtraction;
  return (
    document.includeInSed !== false
    && document.archived !== true
    && document.deletedAt == null
    && !(document.tags ?? []).includes('library-index')
    && typeof sha256 === 'string'
    && SHA256_PATTERN.test(sha256)
    && extraction?.status === 'complete'
    && extraction.extractorVersion === GENERATION_SCREENING_EXTRACTOR_VERSION
    && extraction.documentSha256?.toLowerCase() === sha256
    && extraction.cacheKey === `${sha256}:${GENERATION_SCREENING_EXTRACTOR_VERSION}`
  );
}

function equipmentModelMatches(
  candidate: string,
  input: GenerationImpactScenarioInput,
): boolean {
  const equipment = input.equipmentBasis;
  if (!equipment) return false;
  const normalizedCandidate = normalizedToken(candidate);
  if (normalizedCandidate.length < 3) return false;
  const selected = [
    equipment.catalogId,
    equipment.model,
    `${equipment.manufacturer} ${equipment.model}`,
  ].map(normalizedToken);
  return selected.some(value => (
    value === normalizedCandidate
    || (normalizedCandidate.length >= 4
      && (value.includes(normalizedCandidate) || normalizedCandidate.includes(value)))
  ));
}

function factApplies(
  fact: GenerationScreeningDocumentFact,
  input: GenerationImpactScenarioInput,
): boolean {
  const equipment = input.equipmentBasis;
  if (!equipment) return false;
  if (
    fact.applicability.technologies?.length
    && !fact.applicability.technologies.includes(equipment.technology)
  ) {
    return false;
  }
  if (
    fact.applicability.coolingSystems?.length
    && !fact.applicability.coolingSystems.includes(
      equipment.screeningNormalization.coolingSystem,
    )
  ) {
    return false;
  }
  if (
    fact.applicability.equipmentModels?.length
    && !fact.applicability.equipmentModels.some(model => equipmentModelMatches(model, input))
  ) {
    return false;
  }
  return true;
}

function resolveSingle(
  facts: readonly BoundFact[],
  field: GenerationScreeningDocumentFact['field'],
  conflicts: string[],
  label: string,
): ResolvedValue | undefined {
  const candidates = facts.filter(candidate => candidate.fact.field === field);
  if (candidates.length === 0) return undefined;
  const byValue = new Map<string, BoundFact[]>();
  for (const candidate of candidates) {
    const key = String(candidate.value);
    byValue.set(key, [...(byValue.get(key) ?? []), candidate]);
  }
  if (byValue.size !== 1) {
    conflicts.push(`${label}: conflicting document values require review.`);
    return undefined;
  }
  const agreeing = [...byValue.values()][0]!;
  return { value: agreeing[0]!.value, facts: agreeing };
}

function groupKey(candidate: BoundFact): string {
  return `${candidate.sourceId}:${candidate.fact.applicability.groupId ?? ''}`;
}

function sourceIds(facts: readonly BoundFact[]): string[] {
  return [...new Set(facts.map(candidate => candidate.sourceId))].sort();
}

function resolveAmbientCurve(
  facts: readonly BoundFact[],
  conflicts: string[],
): { points: Array<{ dryBulbC: number; grossOutputFactor: number }>; facts: BoundFact[] } | undefined {
  const groups = new Map<string, BoundFact[]>();
  for (const candidate of facts) {
    if (
      candidate.fact.field !== 'projectDuty.ambientCorrection.point.dryBulbC'
      && candidate.fact.field !== 'projectDuty.ambientCorrection.point.grossOutputFactor'
    ) {
      continue;
    }
    if (!candidate.fact.applicability.groupId) continue;
    groups.set(groupKey(candidate), [...(groups.get(groupKey(candidate)) ?? []), candidate]);
  }

  const curves: Array<{
    signature: string;
    points: Array<{ dryBulbC: number; grossOutputFactor: number }>;
    facts: BoundFact[];
  }> = [];
  for (const groupedFacts of groups.values()) {
    const temperatures = groupedFacts
      .filter(candidate => candidate.fact.field === 'projectDuty.ambientCorrection.point.dryBulbC')
      .sort(factOrder);
    const factors = groupedFacts
      .filter(candidate => candidate.fact.field === 'projectDuty.ambientCorrection.point.grossOutputFactor')
      .sort(factOrder);
    if (temperatures.length < 2 || temperatures.length !== factors.length) continue;
    const points = temperatures
      .map((candidate, index) => ({
        dryBulbC: candidate.value,
        grossOutputFactor: factors[index]!.value,
      }))
      .sort((left, right) => left.dryBulbC - right.dryBulbC);
    if (
      new Set(points.map(point => point.dryBulbC)).size !== points.length
      || points.some((point, index) => index > 0
        && point.dryBulbC <= points[index - 1]!.dryBulbC)
    ) {
      continue;
    }
    curves.push({
      signature: JSON.stringify(points),
      points,
      facts: [...temperatures, ...factors],
    });
  }

  if (curves.length === 0) return undefined;
  const signatures = new Set(curves.map(curve => curve.signature));
  if (signatures.size > 1) {
    conflicts.push('Ambient performance curve: conflicting document curves require review.');
    return undefined;
  }
  return {
    points: curves[0]!.points,
    facts: curves.flatMap(curve => curve.facts),
  };
}

function factOrder(left: BoundFact, right: BoundFact): number {
  const leftOffset = left.fact.provenance.sourceOffset?.start ?? Number.MAX_SAFE_INTEGER;
  const rightOffset = right.fact.provenance.sourceOffset?.start ?? Number.MAX_SAFE_INTEGER;
  return leftOffset - rightOffset || left.fact.id.localeCompare(right.fact.id);
}

function resolveEnvelopes(
  facts: readonly BoundFact[],
  conflicts: string[],
): {
  values: Partial<Record<ProjectDutyEnvelopeKey, { lengthM: number; widthM: number }>>;
  factsByEnvelope: Partial<Record<ProjectDutyEnvelopeKey, BoundFact[]>>;
} {
  const values: Partial<Record<ProjectDutyEnvelopeKey, { lengthM: number; widthM: number }>> = {};
  const factsByEnvelope: Partial<Record<ProjectDutyEnvelopeKey, BoundFact[]>> = {};
  const byEnvelope = new Map<ProjectDutyEnvelopeKey, BoundFact[]>();
  for (const candidate of facts) {
    if (
      candidate.fact.field !== 'projectDuty.installedEnvelope.lengthM'
      && candidate.fact.field !== 'projectDuty.installedEnvelope.widthM'
    ) {
      continue;
    }
    const envelope = ENVELOPE_GROUPS[normalizedToken(
      candidate.fact.applicability.groupId ?? '',
    )];
    if (!envelope) continue;
    byEnvelope.set(envelope, [...(byEnvelope.get(envelope) ?? []), candidate]);
  }

  for (const [envelope, groupedFacts] of byEnvelope) {
    const length = resolveSingle(
      groupedFacts,
      'projectDuty.installedEnvelope.lengthM',
      conflicts,
      `${envelope} length`,
    );
    const width = resolveSingle(
      groupedFacts,
      'projectDuty.installedEnvelope.widthM',
      conflicts,
      `${envelope} width`,
    );
    if (!length || !width) continue;
    values[envelope] = { lengthM: length.value, widthM: width.value };
    factsByEnvelope[envelope] = [...length.facts, ...width.facts];
  }
  return { values, factsByEnvelope };
}

function resolveAcoustic(
  facts: readonly BoundFact[],
  conflicts: string[],
): {
  value: ProjectDutyDocumentPrefill['values']['acoustic'];
  facts: BoundFact[];
} | undefined {
  const soundPower = resolveSingle(
    facts,
    'projectDuty.acoustics.soundPowerDbA',
    conflicts,
    'Acoustic sound power',
  );
  const referenceSpl = resolveSingle(
    facts,
    'projectDuty.acoustics.referenceSplDbA',
    conflicts,
    'Acoustic reference SPL',
  );
  if (soundPower && referenceSpl) {
    conflicts.push('Acoustic datum: sound-power and reference-SPL facts both need review.');
    return undefined;
  }
  if (soundPower) {
    return {
      value: { mode: 'sound-power', levelDbA: soundPower.value },
      facts: soundPower.facts,
    };
  }
  if (!referenceSpl) return undefined;
  const referenceDistance = resolveSingle(
    facts,
    'projectDuty.acoustics.referenceDistanceM',
    conflicts,
    'Acoustic reference distance',
  );
  if (!referenceDistance) return undefined;
  return {
    value: {
      mode: 'reference-spl',
      levelDbA: referenceSpl.value,
      referenceDistanceM: referenceDistance.value,
    },
    facts: [...referenceSpl.facts, ...referenceDistance.facts],
  };
}

/**
 * Resolve only deterministically validated project-duty facts. The result is a
 * review draft, never an applied basis: callers must keep it dirty and require
 * the existing source-authority verification and Apply workflow.
 */
export function resolveProjectDutyDocumentPrefill({
  input,
  documents,
  evidenceMode,
}: ResolveProjectDutyDocumentPrefillOptions): ProjectDutyDocumentPrefill {
  const empty: ProjectDutyDocumentPrefill = {
    values: {},
    fieldSourceIds: {},
    sources: [],
    reviewOnlyCcsFacts: [],
    conflicts: [],
  };
  if (evidenceMode !== 'docs-included' || !input.equipmentBasis || input.projectDutyBasis) {
    return empty;
  }

  const facts: BoundFact[] = [];
  for (const document of documents) {
    if (!documentIsEligible(document)) continue;
    const sha256 = document.contentHash!.toLowerCase();
    for (const fact of document.generationScreeningExtraction!.facts) {
      if (
        fact.status !== 'project-duty-review'
        || fact.target !== 'project-duty-draft'
        || typeof fact.canonical?.value !== 'number'
        || !Number.isFinite(fact.canonical.value)
        || !factApplies(fact, input)
      ) {
        continue;
      }
      facts.push({
        document,
        fact,
        value: fact.canonical.value,
        sourceId: sourceId(sha256),
      });
    }
  }

  const conflicts: string[] = [];
  const values: ProjectDutyDocumentPrefill['values'] = {};
  const fieldSourceIds: ProjectDutyDocumentPrefill['fieldSourceIds'] = {};
  const usedFacts: BoundFact[] = [];

  const assignSingle = (
    target: 'referenceIsoGrossMWPerBlock' | 'auxiliaryLoadMWPerBlock' | 'heatRateBtuPerKWhHhv',
    field: GenerationScreeningDocumentFact['field'],
    label: string,
  ) => {
    const resolved = resolveSingle(facts, field, conflicts, label);
    if (!resolved) return;
    values[target] = resolved.value;
    fieldSourceIds[target] = sourceIds(resolved.facts);
    usedFacts.push(...resolved.facts);
  };
  assignSingle(
    'referenceIsoGrossMWPerBlock',
    'projectDuty.referenceIsoGrossMWPerBlock',
    'Reference ISO gross output',
  );
  assignSingle(
    'auxiliaryLoadMWPerBlock',
    'projectDuty.auxiliaryLoadMWPerBlock',
    'Auxiliary load',
  );
  assignSingle(
    'heatRateBtuPerKWhHhv',
    'projectDuty.heatRateBtuPerKWhHhv',
    'HHV heat rate',
  );

  const ambient = resolveAmbientCurve(facts, conflicts);
  if (ambient) {
    values.ambientCurve = ambient.points;
    fieldSourceIds.ambientCurve = sourceIds(ambient.facts);
    usedFacts.push(...ambient.facts);
  }

  const withdrawal = resolveSingle(
    facts,
    'projectDuty.water.withdrawalGalPerMWhGross',
    conflicts,
    'Generation water withdrawal',
  );
  const consumption = resolveSingle(
    facts,
    'projectDuty.water.consumptionGalPerMWhGross',
    conflicts,
    'Generation water consumption',
  );
  if (withdrawal && consumption && withdrawal.value >= consumption.value) {
    values.water = {
      withdrawalGalPerMWhGross: withdrawal.value,
      consumptionGalPerMWhGross: consumption.value,
    };
    const ids = sourceIds([...withdrawal.facts, ...consumption.facts]);
    fieldSourceIds.waterMode = ids;
    fieldSourceIds.waterWithdrawal = ids;
    fieldSourceIds.waterConsumption = ids;
    usedFacts.push(...withdrawal.facts, ...consumption.facts);
  } else if (withdrawal && consumption) {
    conflicts.push('Generation water: withdrawal is below consumption and was not prefilled.');
  }

  const envelopes = resolveEnvelopes(facts, conflicts);
  if (Object.keys(envelopes.values).length > 0) values.envelopes = envelopes.values;
  for (const [envelope, selectedFacts] of Object.entries(envelopes.factsByEnvelope) as Array<
    [ProjectDutyEnvelopeKey, BoundFact[]]
  >) {
    const ids = sourceIds(selectedFacts);
    fieldSourceIds[`envelopes.${envelope}.lengthM`] = ids;
    fieldSourceIds[`envelopes.${envelope}.widthM`] = ids;
    usedFacts.push(...selectedFacts);
  }

  const acoustic = resolveAcoustic(facts, conflicts);
  if (acoustic?.value) {
    values.acoustic = acoustic.value;
    const ids = sourceIds(acoustic.facts);
    fieldSourceIds.acousticMode = ids;
    fieldSourceIds.acousticLevelDbA = ids;
    if (acoustic.value.referenceDistanceM != null) {
      fieldSourceIds.acousticReferenceDistanceM = ids;
    }
    usedFacts.push(...acoustic.facts);
  }

  const ccsFacts = facts.filter(candidate => candidate.fact.field.startsWith('projectDuty.ccs.'));
  const reviewOnlyCcsFacts = ccsFacts.map(candidate => ({
    field: candidate.fact.field,
    value: candidate.value,
    unit: candidate.fact.canonical!.unit,
    sourceId: candidate.sourceId,
    documentId: candidate.document.id,
    fileName: candidate.document.fileName,
    ...(candidate.fact.provenance.page == null
      ? {}
      : { page: candidate.fact.provenance.page }),
    excerpt: candidate.fact.provenance.excerpt,
  }));

  const usedSourceIds = new Set(usedFacts.map(candidate => candidate.sourceId));
  const sources = documents.flatMap(document => {
    const sha256 = document.contentHash?.toLowerCase();
    if (!sha256 || !usedSourceIds.has(sourceId(sha256))) return [];
    const sourceFacts = usedFacts.filter(candidate => candidate.sourceId === sourceId(sha256));
    return [{
      id: sourceId(sha256),
      documentId: document.id,
      fileName: document.fileName,
      sha256,
      pages: [...new Set(sourceFacts.flatMap(candidate => (
        candidate.fact.provenance.page == null ? [] : [candidate.fact.provenance.page]
      )))].sort((left, right) => left - right),
      excerpts: [...new Set(sourceFacts.map(candidate => candidate.fact.provenance.excerpt))],
    }];
  });

  return {
    values,
    fieldSourceIds,
    sources,
    reviewOnlyCcsFacts,
    conflicts,
  };
}
